/**
 * Нормализация ошибок доступа к Strapi в одном месте (issue #7) и ЕДИНЫЙ
 * interpreter с политиками показа (issue #11).
 *
 * Любая брошенная ошибка (Strapi5Error от useStrapiClient, ofetch FetchError,
 * обычный Error, мусор) превращается в единую форму NormalizedStrapiError;
 * interpretStrapiError() даёт потребителю { status, message, fieldErrors },
 * strapiErrorPolicy() решает, КАК ошибку показывать (fullscreen / toast /
 * field errors / silent) на поверхности, где она возникла.
 */
import type { Strapi5Error } from '@nuxtjs/strapi'

export type NormalizedStrapiError = {
    status: number
    name: string
    message: string
    details: Record<string, unknown>
}

const UNKNOWN_ERROR: NormalizedStrapiError = {
    status: 500,
    name: 'UnknownError',
    message: 'Unknown error',
    details: {},
}

const normalizeErrorBody = (body: unknown): NormalizedStrapiError | undefined => {
    if (!body || typeof body !== 'object') return undefined
    const error = (body as { error?: unknown }).error
    if (!error || typeof error !== 'object') return undefined
    const { status, name, message, details } = error as Record<string, unknown>
    return {
        status: typeof status === 'number' ? status : UNKNOWN_ERROR.status,
        name: typeof name === 'string' ? name : UNKNOWN_ERROR.name,
        message: typeof message === 'string' ? message : UNKNOWN_ERROR.message,
        details: (details && typeof details === 'object' ? details : {}) as Record<string, unknown>,
    }
}

export const normalizeStrapiError = (error: unknown): NormalizedStrapiError => {
    // Strapi5Error (useStrapiClient кидает уже извлечённое тело)
    const direct = normalizeErrorBody(error)
    if (direct) return direct

    if (error instanceof Error) {
        return { status: 500, name: error.name || UNKNOWN_ERROR.name, message: error.message, details: {} }
    }

    if (typeof error === 'string' && error) {
        return { ...UNKNOWN_ERROR, message: error }
    }

    // ofetch FetchError: тело ответа лежит в response._data или data
    if (error && typeof error === 'object') {
        const fetchError = error as { response?: { _data?: unknown }, data?: unknown }
        const fromResponse = normalizeErrorBody(fetchError.response?._data)
        if (fromResponse) {
            const responseStatus = (fetchError.response as { status?: unknown } | undefined)?.status
            return { ...fromResponse, status: typeof responseStatus === 'number' ? responseStatus : fromResponse.status }
        }
        const fromData = normalizeErrorBody(fetchError.data)
        if (fromData) return fromData
    }

    return UNKNOWN_ERROR
}

/** Единая ошибка access-слоя: Error с нормализованными полями и Strapi5Error-совместимой формой .error. */
export class StrapiApiError extends Error {
    readonly status: number
    readonly details: Record<string, unknown>
    /** Strapi5Error-совместимая форма — совместимость с useToastError/хуком strapi:error. */
    readonly error: Strapi5Error['error']
    /**
     * Исходная ошибка до обёртки. useStrapiClient бросает ТОТ ЖЕ объект, что
     * передаёт в хук strapi:error, — по этой ссылке claimStrapiError() достаёт
     * ошибку хука из обёртки transport'а.
     */
    readonly original: unknown

    constructor(normalized: NormalizedStrapiError, original?: unknown) {
        super(normalized.message)
        this.name = normalized.name
        this.status = normalized.status
        this.details = normalized.details
        this.error = { status: normalized.status, name: normalized.name, message: normalized.message, details: normalized.details }
        this.original = original
    }
}

export type StrapiFormError = { message: string, name?: string }

/** Детали валидации Strapi → FormError[] (для NuxtUiForm.setErrors). */
export const toFormErrors = (normalized: Partial<NormalizedStrapiError>): StrapiFormError[] => {
    const errors = (normalized.details as { errors?: { message: string, path?: string[] }[] } | undefined)?.errors
    if (!Array.isArray(errors)) return []
    return errors.map(({ message, path }) => ({ message, name: path?.[0] }))
}

// ---------------------------------------------------------------------------
// Interpreter и политики показа (issue #11)
// ---------------------------------------------------------------------------

/** Результат интерпретации любой ошибки: всё, что нужно поверхностям показа. */
export type InterpretedStrapiError = {
    status: number
    message: string
    fieldErrors: StrapiFormError[]
}

/** Strapi5Error / StrapiApiError / FetchError / Error → { status, message, fieldErrors }. */
export const interpretStrapiError = (error: unknown): InterpretedStrapiError => {
    const normalized = normalizeStrapiError(error)
    return { status: normalized.status, message: normalized.message, fieldErrors: toFormErrors(normalized) }
}

/** Поверхность, на которой ошибка проявилась. */
export type StrapiErrorSurface = 'global' | 'form' | 'login' | 'bulk'

/** Политика показа ошибки. */
export type StrapiErrorDisplay = 'fullscreen' | 'toast' | 'fields' | 'silent'

/**
 * Единая политика показа (interface interpreter'а):
 *  - 404 — fullscreen через showError на любой поверхности (страницы/данные нет);
 *  - global  — всё остальное тостом;
 *  - form    — field errors в форме, БЕЗ глобального тоста; если привязки к
 *              полям нет, форма показать не может — остаётся глобальный тост;
 *  - login   — осмысленный показ в полях формы входа (credentialsFieldErrors);
 *  - bulk    — silent: BulkActionsBar агрегирует ошибки в свой сводный тост.
 */
export const strapiErrorPolicy = (error: InterpretedStrapiError, surface: StrapiErrorSurface): StrapiErrorDisplay => {
    if (error.status === 404) return 'fullscreen'
    switch (surface) {
        case 'global': return 'toast'
        case 'form': return error.fieldErrors.length ? 'fields' : 'toast'
        case 'login': return 'fields'
        case 'bulk': return 'silent'
    }
}

/** Осмысленный показ ошибки входа: сообщение формы раскладывается по полям. */
export const credentialsFieldErrors = (error: InterpretedStrapiError): StrapiFormError[] => {
    const message = error.status === 400 || error.status === 401 ? 'Неверный логин или пароль' : error.message
    // ' ' в password — ошибка без текста: поле подсвечивается, текст не дублируется
    return [{ message, name: 'identifier' }, { message: ' ', name: 'password' }]
}

// ---------------------------------------------------------------------------
// Авто-разлогин: статус × поверхность → действие
// ---------------------------------------------------------------------------

/** Действие глобального обработчика при ошибке API. */
export type StrapiLogoutAction = 'logout' | 'keep'

/**
 * Решение авто-разлогина. 401 от API — единственный сигнал «сессии нет»:
 * токен отсутствует/протух/невалиден, значит надо разлогинить и увести на
 * /login. 403 — аутентифицирован, но прав нет: сессия жива, не разлогиниваем.
 * На login-поверхности разлогин бессмыслен и опасен циклом редиректа —
 * неудачный вход показывает форму сама (см. credentialsFieldErrors).
 */
export const strapiAutoLogoutPolicy = (status: number, surface: StrapiErrorSurface): StrapiLogoutAction => {
    if (status !== 401) return 'keep'
    if (surface === 'login') return 'keep'
    return 'logout'
}

// ---------------------------------------------------------------------------
// Claim: локальные поверхности глушат глобальный показ
// ---------------------------------------------------------------------------

/**
 * Учёт «заявленных» ошибок. Поверхность, взявшая показ на себя (форма — field
 * errors, bulk-панель — сводный тост), помечает ошибку через claimStrapiError()
 * в своём catch, чтобы глобальный обработчик (plugin strapi:error) не дублировал
 * её тостом. Ключ — сам объект ошибки: useStrapiClient передаёт в хук strapi:error
 * и бросает наверх ОДИН И ТОТ ЖЕ объект, а transport хранит его в StrapiApiError.original.
 */
const claimedErrors = new WeakSet<object>()

export const claimStrapiError = (error: unknown): void => {
    let current: unknown = error
    for (let depth = 0; depth < 4 && current && typeof current === 'object'; depth += 1) {
        claimedErrors.add(current as object)
        current = (current as { original?: unknown }).original
    }
}

export const isStrapiErrorClaimed = (error: unknown): boolean =>
    Boolean(error && typeof error === 'object' && claimedErrors.has(error as object))
